import * as React from 'react'
import ButtonBase from '@mui/material/ButtonBase'
import { useTheme } from '@mui/material/styles'
import { dayHeading } from '../../dateFormat'
import { useOpenCreateEvent } from '../createEventContext'
import { startFromOffsetY } from './weekGeometry'

type Props = {
  /** The day this column represents (local midnight). */
  day: Date
  /** Prefix of the owning column's test id, so the target is addressable. */
  testId: string
}

/**
 * SH-2133: the create-on-click target for a day's timed body.
 *
 * An underlay *behind* the tiles rather than a wrapper around the column,
 * because EventTile is itself a ButtonBase and nesting one button inside
 * another is invalid HTML. Tiles keep their own clicks; whatever is not
 * covered by a tile falls through to this.
 *
 * Renders nothing when no create host is mounted — an inert button would still
 * take a tab stop and announce an action that does nothing.
 */
export function TimedColumnCreateOverlay({ day, testId }: Props) {
  const theme = useTheme()
  const openCreate = useOpenCreateEvent()

  const handleCreate = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!openCreate) return
      // Enter/Space on a ButtonBase synthesises a click with detail === 0 and
      // a clientY of 0. Reading it would pin every keyboard create to the top
      // of the grid, so a keyboard activation prefills the date only.
      const fromPointer = e.detail > 0
      const offsetY = e.clientY - e.currentTarget.getBoundingClientRect().top
      openCreate({
        day,
        start: fromPointer ? startFromOffsetY(day, offsetY) : null,
      })
    },
    [openCreate, day],
  )

  if (!openCreate) return null

  return (
    <ButtonBase
      data-test-id={`${testId}-create`}
      onClick={handleCreate}
      aria-label={`Add an event on ${dayHeading(day)}`}
      // ButtonBase ripples by default; across a full-height column that is a
      // large flash on every create click, and the design specifies no visual
      // change on this surface beyond the cursor.
      disableRipple
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        display: 'block',
        cursor: 'pointer',
        '&.Mui-focusVisible': {
          // Inset, because the column clips overflow and an outward outline on
          // an `inset: 0` child would be cut off entirely.
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: -2,
        },
      }}
    />
  )
}
